import { useMemo, useState } from 'react'
import type { JSX } from 'react'

import {
  ChannelAvatar,
  Flex,
  InfoIcon,
  Popover,
  ScrollArea,
  Table,
  Text,
} from '@buffer-mono/popcorn'

import { FilterInput } from '~publish/components/FilterInput'

import type { AssignableChannel } from '../../hooks'
import type { AccessLevel, ProductAccessLevels, ProductKey } from '../../types'

import { AccessLevelSelect } from './AccessLevelSelect'

import styles from './ChannelPermissions.module.css'

// Above this many channels, show a search box to filter the customized list.
// Smaller lists are easy to scan, so the input would just be noise.
const CHANNEL_SEARCH_THRESHOLD = 5

export const PUBLISH_TOOLTIP =
  'Controls posting access: full posting, posts that need approval, or no access.'
export const COMMUNITY_TOOLTIP =
  'Controls Community access: manage comments, view only, or no access.'

/**
 * A product column label paired with an info popover. Shared between the
 * table headers and the collapsed "applies to all" labels so the copy and
 * markup stay consistent.
 *
 * A Popover (click/tap) rather than a Tooltip (hover): it works on touch
 * screens, and unlike the Tooltip it portals into the containing dialog, so
 * it can't disappear behind the dialog overlay (CORE-6242).
 */
export const ProductHeaderLabel = ({
  label,
  tooltip,
}: {
  label: string
  tooltip: string
}): JSX.Element => (
  <span className={styles.productHeaderLabel}>
    {/* Explicit label/default color: don't rely on inheriting the th color,
        which could drift if Popcorn's Table restyles its header cells. */}
    <Text weight="medium" color="default">
      {label}
    </Text>
    <Popover>
      <Popover.Trigger>
        <button
          type="button"
          className={styles.infoButton}
          aria-label={`About ${label} access`}
        >
          <InfoIcon size="xsmall" color="subtle" />
        </button>
      </Popover.Trigger>
      <Popover.Content size="small" side="top" align="center" arrow>
        <Text size="sm">{tooltip}</Text>
      </Popover.Content>
    </Popover>
  </span>
)

interface ChannelPermissionsTableProps {
  channels: AssignableChannel[]
  /** Per-channel levels; missing entries fall back to the caller's default. */
  overrides: Map<string, ProductAccessLevels>
  defaults: ProductAccessLevels
  onChange: (channelId: string, product: ProductKey, level: AccessLevel) => void
}

/**
 * Expanded "Customized" state: one row per channel with independent Publish
 * and Community access selects.
 */
export const ChannelPermissionsTable = ({
  channels,
  overrides,
  defaults,
  onChange,
}: ChannelPermissionsTableProps): JSX.Element => {
  const [search, setSearch] = useState('')
  const showSearch = channels.length > CHANNEL_SEARCH_THRESHOLD

  // View-only filter over the rows; selection is keyed by channel id on the
  // parent, so narrowing what's rendered never affects the stored grants.
  const visibleChannels = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return channels
    return channels.filter(
      ({ channel }) =>
        channel.name.toLowerCase().includes(term) ||
        channel.service.toLowerCase().includes(term),
    )
  }, [channels, search])

  return (
    <Flex direction="column" gap="xs" align="stretch" fullWidth>
      {showSearch && (
        <FilterInput
          value={search}
          onValueChange={setSearch}
          placeholder="Search channels"
          aria-label="Search channels"
        />
      )}
      {/* The ScrollArea owns the table's scrolling (and its border/radius) so
          the vertical scrollbar can start below the sticky header instead of
          overlapping it, and so the thumb color is themeable in dark mode. */}
      <ScrollArea size="small" scrollbars="both" className={styles.tableScroll}>
        <Table stickyHeader className={styles.permissionsTable}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                type="column"
                className={styles.stickyHeaderCell}
              >
                <Text weight="medium" color="default">
                  Channels · {channels.length}
                </Text>
              </Table.HeaderCell>
              <Table.HeaderCell
                type="column"
                className={`${styles.productCol} ${styles.stickyHeaderCell}`}
              >
                <ProductHeaderLabel label="Publish" tooltip={PUBLISH_TOOLTIP} />
              </Table.HeaderCell>
              <Table.HeaderCell
                type="column"
                className={`${styles.productCol} ${styles.stickyHeaderCell}`}
              >
                <ProductHeaderLabel
                  label="Community"
                  tooltip={COMMUNITY_TOOLTIP}
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {visibleChannels.map(({ channel }) => {
              const levels = overrides.get(channel.id) ?? defaults
              return (
                <Table.Row key={channel.id}>
                  <Table.Cell className={styles.channelNameCell}>
                    <Flex
                      align="center"
                      gap="sm"
                      className={styles.channelCell}
                    >
                      <ChannelAvatar
                        src={channel.avatar}
                        alt={channel.name}
                        channel={channel.service}
                        size="small"
                      />
                      <Text className={styles.channelName} title={channel.name}>
                        {channel.name}
                      </Text>
                    </Flex>
                  </Table.Cell>
                  <Table.Cell className={styles.productCol}>
                    <AccessLevelSelect
                      product="publish"
                      value={levels.publish}
                      onChange={(level): void =>
                        onChange(channel.id, 'publish', level)
                      }
                      aria-label={`Publish access for ${channel.name}`}
                    />
                  </Table.Cell>
                  <Table.Cell className={styles.productCol}>
                    <AccessLevelSelect
                      product="comments"
                      value={levels.comments}
                      onChange={(level): void =>
                        onChange(channel.id, 'comments', level)
                      }
                      aria-label={`Community access for ${channel.name}`}
                    />
                  </Table.Cell>
                </Table.Row>
              )
            })}
            {visibleChannels.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={3}>
                  <Text color="subtle">No channels found</Text>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </ScrollArea>
    </Flex>
  )
}
